import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Countries, UsaMethodEvaluations, MethodEvaluations } from '@xbs/xbs-enums';

import { BaseService } from './base/base.service';

import { ListResponse, ObjectResponse } from '../interfaces';

import {
  MethodEvaluation,
  PbaMethodEvaluationResponse,
  TbaMethodEvaluationResponse,
  TransactionMethodEvaluationResponse,
  LegalEntityTransaction,
  EnumValueSet
} from '../models';

import { AppConfig } from './app-config';

@Injectable()
export class TransactionsMethodEvaluationService extends BaseService<MethodEvaluation> {

  constructor(
    protected http: HttpClient,
    protected config: AppConfig
  ) {
    super(http, config);
  }

  public shouldUseUSAMethodEvaluation(jurisdictionIsoCode: string): boolean {
    const usaMethodEvaluationsISOCodes = [Countries.ByIso.USA.IsoCode, Countries.ByIso.TWN.IsoCode];
    return usaMethodEvaluationsISOCodes.includes(jurisdictionIsoCode);
  }

  public getTransactionMethodEvaluations(srcJurisdictionIsoCode: string, destJurisdictionIsoCode: string): EnumValueSet[][] {
    const jurisdictionIsoCodes = [srcJurisdictionIsoCode, destJurisdictionIsoCode];
    const jurisdictionMethodEvaluationSets: EnumValueSet[][] = jurisdictionIsoCodes.map((isoCode) => {
      return this.shouldUseUSAMethodEvaluation(isoCode) ? UsaMethodEvaluations.AsOrderedArray : MethodEvaluations.AsOrderedArray;
    });
    
    jurisdictionMethodEvaluationSets.forEach((evaluation) => {
      evaluation.sort((eval1, eval2) => {
        return eval1.Name > eval2.Name ? 1 : -1;
      });
    });

    return jurisdictionMethodEvaluationSets;
  }

  public getIsoCodeFromLegalEntityTransactions(legalEntityTransactions: LegalEntityTransaction[], isSource = true): string {
    let taxJurisdictionIsoCode = '';

    if (legalEntityTransactions) {
      const transactionToCheck = legalEntityTransactions.find(transaction => transaction.isSource === isSource);
      taxJurisdictionIsoCode = transactionToCheck ? transactionToCheck.entity.taxJurisdiction.isoCode : '';
    }

    return taxJurisdictionIsoCode;
  }

  public getMethodEvaluationTranslationKey(legalEntityTransactions: LegalEntityTransaction[], methodId: number, isSource: boolean): string {
    let methodTranslationKey = '';

    if (legalEntityTransactions) {
      const currentTransactionIsoCode = this.getIsoCodeFromLegalEntityTransactions(legalEntityTransactions, isSource);
      const correctMethodEvaluations = this.shouldUseUSAMethodEvaluation(currentTransactionIsoCode) ? UsaMethodEvaluations : MethodEvaluations;

      methodTranslationKey = correctMethodEvaluations.ById[methodId] && correctMethodEvaluations.ById[methodId].Value || '';
    }

    return methodTranslationKey;
  }

  public getTbaMethodEvaluations(transId: number): Observable<ListResponse<TbaMethodEvaluationResponse>> {
    return this.http
      .get<ListResponse<TbaMethodEvaluationResponse>>(`${this.getResourceUrl()}${transId}/tbas/method-evaluations`);
  }

  public getPbaMethodEvaluations(transId: number): Observable<ListResponse<PbaMethodEvaluationResponse>> {
    return this.http
      .get<ListResponse<PbaMethodEvaluationResponse>>(`${this.getResourceUrl()}${transId}/pbas/method-evaluations`);
  }

  public saveMethodEvaluation(
    transactionId: number,
    objectId: number,
    data: MethodEvaluation,
    isPba = false
  ): Observable<ObjectResponse<TransactionMethodEvaluationResponse>> {
    const routeParam = isPba ? 'pbas' : 'tbas';
    const url = `${this.getResourceUrl()}${transactionId}/${routeParam}/${objectId}/method-evaluation`;

    return this.http.put<ObjectResponse<TransactionMethodEvaluationResponse>>(url, data);
  }

  protected getResourceUrl(): string {
    return `${this.baseUrl}transactions/`;
  }
}
